import React from 'react';
import {useAuthorization} from '../../../features/auth/hooks/useAuthorization';
import {useSecurityAudit} from '../../../features/auth/hooks/useSecurityAudit';
import {PERMISSIONS} from '../../../constants/permissions';

interface PermissionGateProps {
  children: React.ReactNode;
  permission?: string;
  permissions?: string[];
  requireAll?: boolean; // If true, user must have ALL permissions; if false, user needs ANY permission
  fallback?: React.ReactNode;
  showLoading?: boolean;
}

/**
 * PermissionGate - A component that conditionally renders children based on user permissions
 *
 * @param children - Content to render if user has required permissions
 * @param permission - Single permission to check
 * @param permissions - Array of permissions to check
 * @param requireAll - If true, user must have ALL permissions; if false, user needs ANY permission
 * @param fallback - Content to render if user lacks permissions (optional)
 * @param showLoading - Whether to show loading state while checking permissions
 */
const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  permission,
  permissions,
  requireAll = false,
  fallback = null,
  showLoading = false
}) => {
  const {hasPermission, hasAnyPermission, user} = useAuthorization();
  const {logPermissionDenied} = useSecurityAudit();

  // Show loading if requested and user data is not yet loaded
  if (showLoading && !user) {
    return <div className="animate-pulse bg-gray-200 rounded h-4 w-20"></div>;
  }

  let hasAccess = false;

  // Check single permission
  if (permission) {
    hasAccess = hasPermission(permission);
  }
  // Check multiple permissions
  else if (permissions && permissions.length > 0) {
    if (requireAll) {
      // User must have ALL permissions
      hasAccess = permissions.every(perm => hasPermission(perm));
    } else {
      // User needs ANY permission
      hasAccess = hasAnyPermission(permissions);
    }
  }
  // If no permissions specified, deny access
  else {
    hasAccess = false;
  }

  // Log permission denial for security audit (only for critical permissions)
  if (!hasAccess && (permission || permissions)) {
    const deniedPermission = permission || (permissions && permissions[0]);
    if (deniedPermission && isCriticalPermission(deniedPermission)) {
      logPermissionDenied(deniedPermission);
    }
  }

  // Render children if user has access, otherwise render fallback
  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

/**
 * Check if permission is critical and should be logged
 */
function isCriticalPermission (permission: string): boolean {
  const criticalPermissions = [
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.ASSIGN_ROLES,
    PERMISSIONS.SYSTEM_SETTINGS,
    PERMISSIONS.VIEW_AUDIT_LOGS,
    PERMISSIONS.DELETE_USERS,
    PERMISSIONS.MANAGE_PAYMENTS,
    PERMISSIONS.VIEW_FINANCIAL_REPORTS
  ];

  return criticalPermissions.includes(permission);
}

export default PermissionGate;
